import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  globalTheme,
  Icon,
  IconSize,
  Text,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { getPermissionMessage, useAppPermissions, useAppUser, useDebounce } from '@bechtel/ht-clients-common';
import {
  AuthPermission,
  BulkRequisitionInput,
  BulkRequisitionResult,
  CreateRequisitionToolRoomLockInput,
  CreateRequisitionToolRoomLockResult,
  DeleteRequisitionToolRoomLockInput,
  DeleteRequisitionToolRoomLockResult,
  FilterConditionInput,
  FilterInput,
  OperatorType,
  Requisition,
  RequisitionCatalogComment,
  RequisitionStatus,
  RequisitionToolCatalogInput,
  SearchInput,
  SearchRequisitionResult,
  SearchToolCatalogResult,
  SortType,
  SubmitRequisitionInput,
  SubmitRequisitionResult,
  ToolCatalog,
  ToolRoom,
} from '@bechtel/ht-types';
import { filter, findIndex, get, map } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { client } from '~/apollo-client';
import { Backdrop } from '~/components/backdrop';
import { RequisitionCommentDialog } from '~/components/molecules/comment-dialog-requisition';
import {
  LOCK_REQUISITION_TOOL_ROOM,
  SAVE_BULK_REQUISITION,
  SEARCH_REQUISITIONS,
  SEARCH_TOOL_CATALOGS_FOR_REQUISITION,
  SUBMIT_REQUISITION,
  TOOL_CATALOGS_FOR_REQUISITION_COUNT_FOR_SEARCH,
  UNLOCK_REQUISITION_TOOL_ROOM,
} from '~/gql';
import { ErrorModal } from '../../../components';
import { ActiveRequisitionHeader } from './active-requisition-header';
import { ConfirmLockToolRoomDialog } from './confirm-lock-toolroom';
import ItemRow from './item-row';
import { ActiveRequisitionWrapper, LockIcon, LockIconStyled, ReqFooterStyled, TableStyled } from './styled';

type CommentDataProps = {
  commentType: string;
  toolRoomId: string;
  selectedToolCatalog: ToolCatalog | null;
  comments: RequisitionCatalogComment[];
  isLocked: boolean;
  stats: { QOH: number; QOR: number; QN: number; qtyReq: number };
};

const ActiveRequisition = () => {
  const [searchText, setSearchText] = useState('');
  const [itemsToSave, setItemsToSave] = useState<RequisitionToolCatalogInput[]>([]);
  const debouncedSearchText = useDebounce(searchText, 500);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [projectOnlyCatalogs, setProjectOnlyCatalogs] = useState(false);
  const dispatchToast = useToastsDispatch();
  const { appUser } = useAppUser();
  const { hasPermission } = useAppPermissions();
  const [selectedToolRoom, setSelectedToolRoom] = useState<ToolRoom | null>(null);
  const canLockToolRoomPermission = hasPermission(AuthPermission.ADD_REQUISITION_TOOL_ROOM_LOCK);
  const canSaveRequisitionPermission = hasPermission(AuthPermission.SAVE_REQUISITION);
  const canDeleteLockToolRoomPermission = hasPermission(AuthPermission.DELETE_REQUISITION_TOOL_ROOM_LOCK);
  const { t } = useTranslation();
  const unsavedItemsRef = useRef<HTMLElement>(null);
  const [updateCommentData, setUpdateCommentData] = useState<CommentDataProps>({
    commentType: '',
    toolRoomId: '',
    selectedToolCatalog: null,
    comments: [],
    isLocked: false,
    stats: { QOH: 0, QOR: 0, QN: 0, qtyReq: 0 },
  });
  const navigate = useNavigate();

  const [submitRequisition, { loading: submitReqLoading }] = useMutation<
    { submitRequisition: SubmitRequisitionResult },
    { submitRequisitionInput: SubmitRequisitionInput }
  >(SUBMIT_REQUISITION);

  const filterValues = useMemo(() => {
    const or: FilterConditionInput[] = [];
    const and: FilterConditionInput[] = [{ field: 'isActive', operator: OperatorType.EQUAL, value: '1' }];
    if (debouncedSearchText) {
      or.push(
        {
          field: 'name',
          operator: OperatorType.LIKE,
          value: debouncedSearchText,
        },
        {
          field: 'bscnPartNumber',
          operator: OperatorType.LIKE,
          value: debouncedSearchText,
        }
      );
    }
    const filterInputObj: FilterInput = {
      or: or,
      and: and,
    };
    return { ...filterInputObj };
  }, [debouncedSearchText]);

  const [searchRequisitions, { data: requisitions, loading: loadingRequisitions }] = useLazyQuery<
    {
      searchRequisitions: SearchRequisitionResult;
    },
    { searchInput: SearchInput }
  >(SEARCH_REQUISITIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const requisitionsList: Requisition[] = get(requisitions, 'searchRequisitions.items', []);

  const [getToolCatalogsForRequisition, { data, loading, fetchMore }] = useLazyQuery<
    {
      searchToolCatalogsForRequisition: SearchToolCatalogResult;
    },
    { searchInput: SearchInput; filterByProjectToolCatalog: boolean }
  >(SEARCH_TOOL_CATALOGS_FOR_REQUISITION, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const [getToolCatalogsCountForRequisitionSearch, { loading: totalLoading, data: totalData }] = useLazyQuery<
    {
      toolCatalogsCountForRequisitionSearch: number;
    },
    { searchInput: SearchInput; filterByProjectToolCatalog: boolean }
  >(TOOL_CATALOGS_FOR_REQUISITION_COUNT_FOR_SEARCH);
  const [createRequisitionToolRoomLock, { data: lockToolRoomData, loading: lockingToolRoom }] = useMutation<
    { createRequisitionToolRoomLock: CreateRequisitionToolRoomLockResult },
    { createRequisitionToolRoomLockInput: CreateRequisitionToolRoomLockInput }
  >(LOCK_REQUISITION_TOOL_ROOM);

  const [deleteRequisitionToolRoomLock, { data: unlockToolRoomData, loading: unlockingToolRoom }] = useMutation<
    { deleteRequisitionToolRoomLock: DeleteRequisitionToolRoomLockResult },
    { deleteRequisitionToolRoomLockInput: DeleteRequisitionToolRoomLockInput }
  >(UNLOCK_REQUISITION_TOOL_ROOM);

  const [saveBulkRequisition, { loading: saveRequisitionLoading }] = useMutation<
    { saveBulkRequisition: BulkRequisitionResult },
    { bulkRequisitionInput: BulkRequisitionInput }
  >(SAVE_BULK_REQUISITION);

  const updateBulkRequisitionData = () => {
    if (saveRequisitionLoading) return;
    if (!canSaveRequisitionPermission) {
      setErrorMessage(getPermissionMessage('MISSING_REQUIRED_PERMISSION:SAVE_REQUISITION'));
      setItemsToSave([]);
      return;
    }
    saveBulkRequisition({
      variables: {
        bulkRequisitionInput: {
          requisitionId: requisitionsList[0]?.id,
          requisitionToolCatalogs: itemsToSave,
        },
      },
      onCompleted: (data) => {
        if (data?.saveBulkRequisition?.error) {
          setItemsToSave([]);
          setErrorMessage(t(data?.saveBulkRequisition?.error));
        } else {
          setItemsToSave([]);
          const toastContent: ToastAction = {
            type: 'ADD_TOAST',
            payload: {
              toastType: ToastType.SUCCESS,
              content: `${t('BULK_REQUISITION_SAVED_SUCCESSFULLY')} `,
              autoDismissTimeout: 5000,
            },
          };
          dispatchToast(toastContent);
        }
      },
      refetchQueries: ['SearchToolCatalogsForRequisition'],

      onError: (err) => {
        setItemsToSave([]);
      },
    });
  };

  const handleScroll = (e) => {
    if (!totalData?.toolCatalogsCountForRequisitionSearch) return;

    if (
      e.target.scrollTop + e.target.clientHeight + 50 >= e.target.scrollHeight &&
      !loading &&
      !totalLoading &&
      toolCatalogs?.length < totalData?.toolCatalogsCountForRequisitionSearch
    ) {
      fetchMore({
        variables: {
          searchInput: {
            filterInput: filterValues,
            paginationInput: {
              limit: 20,
              offset: toolCatalogs.length,
            },
            sortInput: [
              {
                field: 'updatedDate',
                sort: SortType.DESC,
              },
            ],
          },
          filterByProjectToolCatalog: projectOnlyCatalogs,
        },
      });
    }
  };

  const toolCatalogs: ToolCatalog[] = get(data, 'searchToolCatalogsForRequisition.items', []);

  useEffect(() => {
    getToolCatalogsForRequisition({
      variables: {
        searchInput: {
          filterInput: filterValues,
          sortInput: [
            {
              field: 'updatedDate',
              sort: SortType.DESC,
            },
          ],
          paginationInput: {
            offset: 0,
            limit: 20,
          },
        },
        filterByProjectToolCatalog: projectOnlyCatalogs,
      },
    });
  }, [debouncedSearchText, projectOnlyCatalogs]);

  useEffect(() => {
    getToolCatalogsCountForRequisitionSearch({
      variables: {
        searchInput: {
          filterInput: filterValues,
          sortInput: [],
          paginationInput: {
            offset: 0,
            limit: 20,
          },
        },
        filterByProjectToolCatalog: projectOnlyCatalogs,
      },
    });
  }, [debouncedSearchText, projectOnlyCatalogs]);

  useEffect(() => {
    if (!itemsToSave.length || saveRequisitionLoading) return;
    const interval = setInterval(updateBulkRequisitionData, 3500);
    return () => {
      clearInterval(interval);
    };
  }, [itemsToSave]);

  useEffect(() => {
    searchRequisitions({
      variables: {
        searchInput: {
          sortInput: [],
          filterInput: {
            and: [
              {
                field: 'status',
                value: RequisitionStatus.ACTIVE,
                operator: OperatorType.EQUAL,
              },
            ],
          },
        },
      },
    });
  }, [searchRequisitions, lockToolRoomData, unlockToolRoomData]);

  const lockRequisitionToolRoom = (toolRoomId) => {
    createRequisitionToolRoomLock({
      variables: {
        createRequisitionToolRoomLockInput: {
          requisitionId: requisitionsList[0]?.id,
          toolRoomId: toolRoomId,
        },
      },
      onCompleted: (data) => {
        if (data?.createRequisitionToolRoomLock?.error) {
          setErrorMessage(t(data?.createRequisitionToolRoomLock?.error));
        } else {
          const toastContent: ToastAction = {
            type: 'ADD_TOAST',
            payload: {
              toastType: ToastType.SUCCESS,
              content: `${t('LOCKED_REQUISITION_TOOL_ROOM_SUCCESSFULLY')} `,
              autoDismissTimeout: 5000,
            },
          };
          dispatchToast(toastContent);
        }
      },
      onError: (err) => {
        setErrorMessage(getPermissionMessage(err?.message));
      },
    });
  };

  const getRequisitiomToolRoomLockId = (toolRoomId) => {
    const lockedToolRooms =
      requisitionsList.length > 0 &&
      requisitionsList[0]?.requisitionToolRoomLocks?.filter((item) => {
        return toolRoomId == item?.toolRoomId;
      });
    return lockedToolRooms?.[0].id;
  };

  const unLockRequisitionToolRoom = (toolRoomId) => {
    deleteRequisitionToolRoomLock({
      variables: {
        deleteRequisitionToolRoomLockInput: {
          id: getRequisitiomToolRoomLockId(toolRoomId),
        },
      },
      onCompleted: (data) => {
        if (data?.deleteRequisitionToolRoomLock?.errors && data?.deleteRequisitionToolRoomLock?.errors[0]) {
          setErrorMessage(t(data?.deleteRequisitionToolRoomLock?.errors[0]));
        } else {
          const toastContent: ToastAction = {
            type: 'ADD_TOAST',
            payload: {
              toastType: ToastType.SUCCESS,
              content: `${t('UNLOCKED_REQUISITION_TOOL_ROOM_SUCCESSFULLY')} `,
              autoDismissTimeout: 5000,
            },
          };
          dispatchToast(toastContent);
        }
      },
      onError: (err) => {
        setErrorMessage(getPermissionMessage(err?.message));
      },
    });
  };

  const isToolRoomLocked = useCallback(
    (toolRoom: ToolRoom | null) => {
      const lockedToolRooms = filter(
        requisitionsList[0]?.requisitionToolRoomLocks,
        (item) => toolRoom?.id === item?.toolRoomId
      );
      return lockedToolRooms?.length > 0;
    },
    [requisitionsList]
  );

  const handleLockPress = (toolRoom) => {
    if (!isToolRoomLocked(toolRoom)) {
      lockRequisitionToolRoom(toolRoom?.id);
    } else {
      unLockRequisitionToolRoom(toolRoom?.id);
    }
  };

  //for getting name of user who locked tool room
  const getToolRoomLockedByUserName = (toolRoom) => {
    const lockedToolRooms = filter(
      requisitionsList[0]?.requisitionToolRoomLocks,
      (item) => toolRoom.id === item?.toolRoomId
    );
    return lockedToolRooms?.length > 0
      ? ` ${lockedToolRooms[0]?.lockedByUser?.lastName} , ${lockedToolRooms[0]?.lockedByUser?.firstName} `
      : '';
  };

  //for checking tool roomlock icon clickable or not
  const checkToolRoomLockClickEnabled = (toolRoom) => {
    return (
      (isToolRoomLocked(toolRoom) && canDeleteLockToolRoomPermission) ||
      (!isToolRoomLocked(toolRoom) && canLockToolRoomPermission)
    );
  };

  const handleSubmitRequisition = async () => {
    await submitRequisition({
      variables: {
        submitRequisitionInput: {
          id: requisitionsList[0].id,
        },
      },
      onCompleted: (data) => {
        if (data.submitRequisition.error) {
          setErrorMessage(t(data.submitRequisition.error));
        } else if (data?.submitRequisition?.requisition) {
          const toastContent: ToastAction = {
            type: 'ADD_TOAST',
            payload: {
              toastType: ToastType.SUCCESS,
              content: t('REQUISITION_SUBMITTED_SUCCESSFULLY'),
              autoDismissTimeout: 5000,
            },
          };
          client.cache.evict({ fieldName: 'searchToolCatalogsForRequisition' });
          client.cache.evict({ fieldName: 'searchRequisitions' });
          client.cache.evict({ fieldName: 'searchRequisitionToolCatalogs' });
          dispatchToast(toastContent);
          navigate('history');
        }
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        itemsToSave &&
        itemsToSave.length > 0 &&
        unsavedItemsRef.current &&
        !unsavedItemsRef.current.contains(event.target)
      ) {
        alert(t('UNSAVED_CHANGES_MESSAGE'));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [unsavedItemsRef, itemsToSave]);

  return (
    <Box flexDirection={{ sm: 'column' }} css={{ overflow: 'hidden' }} flexGrow={{ sm: '1' }}>
      <ActiveRequisitionHeader
        searchText={searchText}
        setSearchText={(text) => setSearchText(text)}
        projectOnlyCatalogs={projectOnlyCatalogs}
        setProjectOnlyCatalogs={(val) => setProjectOnlyCatalogs(val)}
      />
      {(loading ||
        lockingToolRoom ||
        unlockingToolRoom ||
        loadingRequisitions ||
        saveRequisitionLoading ||
        submitReqLoading) && <Backdrop />}
      <ActiveRequisitionWrapper onScroll={handleScroll}>
        <TableStyled>
          <thead>
            <tr>
              <th style={{ minWidth: '340px', zIndex: 1 }}>{t('ITEM_DESCRIPTION')}</th>
              <th style={{ left: '340px', zIndex: 1 }}>{t('QOH')}</th>
              <th style={{ left: '440px', zIndex: 1 }}>{t('QOR')}</th>
              <th style={{ left: '540px', zIndex: 1 }}>{t('QTY_NEEDED')}</th>
              {appUser?.toolRooms &&
                appUser.toolRooms.length > 0 &&
                map(appUser?.toolRooms, (toolRoom) => {
                  return (
                    <th key={toolRoom?.id} style={{ minWidth: '160px' }}>
                      <Box
                        justifyContent={{ sm: 'space-between' }}
                        alignItems={{ sm: 'center' }}
                        css={{ lineHeight: 1.2 }}
                      >
                        <span>{toolRoom?.name}</span>
                        <LockIconStyled
                          title={getToolRoomLockedByUserName(toolRoom)}
                          ml={{ sm: '400' }}
                          onClick={() => {
                            if (checkToolRoomLockClickEnabled(toolRoom)) {
                              if (isToolRoomLocked(toolRoom)) {
                                handleLockPress(toolRoom);
                              } else {
                                setSelectedToolRoom(toolRoom);
                                setConfirmMessage(`${t('LOCK_REQUISITION_TOOL_ROOM_CONFIRMATION')} ${toolRoom?.name}`);
                              }
                            }
                          }}
                          hasPermission={checkToolRoomLockClickEnabled(toolRoom)}
                        >
                          <LockIcon
                            icon="thumbs-up"
                            iconStyle="solid"
                            fillColor={isToolRoomLocked(toolRoom) ? 'blue.800' : 'grey.400'}
                            canLockToolRoom={canLockToolRoomPermission}
                            isLocked={isToolRoomLocked(toolRoom)}
                          />
                        </LockIconStyled>
                      </Box>
                    </th>
                  );
                })}
              <th style={{ minWidth: '120px' }}>{t('TOTAL_REQUESTED')}</th>
              <th style={{ minWidth: '140px' }}>{t('PREVIOUS_REQUEST')}</th>
            </tr>
          </thead>
          <tbody>
            {map(toolCatalogs, (toolCatalog, index) => (
              <ItemRow
                toolCatalog={toolCatalog}
                key={toolCatalog.id}
                onCommentClick={(toolRoomId, comments, type, stats, isLocked) => {
                  setUpdateCommentData({
                    toolRoomId,
                    comments,
                    commentType: type,
                    isLocked,
                    selectedToolCatalog: toolCatalog,
                    stats,
                  });
                  setShowCommentDialog(true);
                }}
                updateItemsToSave={(data) => {
                  const existingItemIndex = findIndex(itemsToSave, (item) => item.toolCatalogId === data.toolCatalogId);
                  if (existingItemIndex > -1) {
                    const updatedArray = [...itemsToSave]; // Create a copy of the array
                    updatedArray[existingItemIndex] = {
                      ...updatedArray[existingItemIndex],
                      requisitionCatalogToolRooms: data.requisitionCatalogToolRooms,
                    }; // Update the object at the given index
                    setItemsToSave(updatedArray);
                  } else {
                    setItemsToSave((prev) => [...prev, data]);
                  }
                }}
                itemsToSaveCount={itemsToSave.length}
                requisition={requisitionsList[0]}
              />
            ))}
          </tbody>
        </TableStyled>
      </ActiveRequisitionWrapper>
      {hasPermission(AuthPermission.SAVE_REQUISITION) && (
        <ReqFooterStyled>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            size={ButtonSize.MEDIUM}
            css={{ width: '110px' }}
            onClick={handleSubmitRequisition}
          >
            <Icon icon="check" size={IconSize.MEDIUM} style={{ marginRight: globalTheme.spacings[400] }} />
            <Text>{t('SUBMIT')}</Text>
          </Button>
        </ReqFooterStyled>
      )}
      {errorMessage != '' && (
        <ErrorModal
          close={() => {
            setErrorMessage('');
          }}
          message={errorMessage}
          closeText={t('Close')}
          title={t('ERROR_MESSAGE')}
        />
      )}
      {confirmMessage != '' && (
        <ConfirmLockToolRoomDialog
          toolRoom={selectedToolRoom?.name}
          onClose={() => {
            setConfirmMessage('');
          }}
          onConfirm={() => {
            setConfirmMessage('');
            handleLockPress(selectedToolRoom);
          }}
        />
      )}
      {updateCommentData.selectedToolCatalog && (
        <RequisitionCommentDialog
          isLocked={updateCommentData.isLocked}
          updateCommentData={updateCommentData}
          requisitionId={requisitionsList[0]?.id}
          saveBulkRequisition={saveBulkRequisition}
          commentList={updateCommentData.comments}
          toolCatalog={updateCommentData.selectedToolCatalog}
          visible={showCommentDialog}
          onClose={() => {
            setShowCommentDialog(false);
          }}
        />
      )}
    </Box>
  );
};

export default ActiveRequisition;
